import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  fetchMoriartyEnvironments,
  fetchMoriartyEnvironment,
  fetchMoriartyProntoStatuses,
  fetchMoriartyConfig,
  keepMoriartyEnvironmentAwake,
  updateMoriartyConfig,
  spawnMoriartyEnvironment,
  destroyMoriartyEnvironment,
  sendMoriartyEnvironmentPrompt,
  sleepMoriartyEnvironment,
  syncMoriartyModelCredentials,
  wakeMoriartyEnvironment,
} from "@/lib/api";
import type { MoriartySpawnRequest } from "@/lib/types";

const MORIARTY_PRONTO_REFRESH_MS = 120_000;

export function useMoriartyEnvironments(filters?: {
  status?: string;
  includeTerminated?: boolean;
}) {
  return useQuery({
    queryKey: ["moriarty-environments", filters],
    queryFn: () => fetchMoriartyEnvironments(filters),
    refetchInterval: (query) => {
      const hasStarting = query.state.data?.environments.some((e) =>
        ["starting", "stopping", "resuming"].includes(e.status),
      );
      // Poll every 5s if something is booting, otherwise every 30s
      return hasStarting ? 5000 : 30000;
    },
  });
}

export function useMoriartyEnvironment(id: string | null) {
  return useQuery({
    queryKey: ["moriarty-environment", id],
    queryFn: () => fetchMoriartyEnvironment(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.environment.status;
      if (status === "starting" || status === "stopping" || status === "resuming") return 5000;
      if (status === "ready" || status === "warning") return 30000;
      return false;
    },
  });
}

export function useMoriartyProntoStatuses(enabled: boolean) {
  return useQuery({
    queryKey: ["moriarty-pronto-statuses"],
    queryFn: fetchMoriartyProntoStatuses,
    enabled,
    staleTime: MORIARTY_PRONTO_REFRESH_MS,
    refetchInterval: enabled ? MORIARTY_PRONTO_REFRESH_MS : false,
    retry: 1,
  });
}

export function useMoriartyConfig() {
  return useQuery({
    queryKey: ["moriarty-config"],
    queryFn: fetchMoriartyConfig,
    staleTime: 60000,
  });
}

export function useUpdateMoriartyConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: Record<string, string>) => updateMoriartyConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moriarty-config"] });
    },
  });
}

export function useSpawnMoriarty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MoriartySpawnRequest) => spawnMoriartyEnvironment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moriarty-environments"] });
    },
  });
}

export function useDestroyMoriarty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => destroyMoriartyEnvironment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moriarty-environments"] });
    },
  });
}

export function useSendMoriartyPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sendMoriartyEnvironmentPrompt(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["moriarty-environment", id] });
      queryClient.invalidateQueries({ queryKey: ["moriarty-environments"] });
    },
  });
}

function useMoriartyLifecycleMutation(action: "sleep" | "wake") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      action === "sleep" ? sleepMoriartyEnvironment(id) : wakeMoriartyEnvironment(id),
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: ["moriarty-environment", id] });
      queryClient.invalidateQueries({ queryKey: ["moriarty-environments"] });
    },
  });
}

export function useSleepMoriarty() {
  return useMoriartyLifecycleMutation("sleep");
}

export function useWakeMoriarty() {
  return useMoriartyLifecycleMutation("wake");
}

export function useKeepMoriartyAwake() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => keepMoriartyEnvironmentAwake(id),
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: ["moriarty-environment", id] });
      queryClient.invalidateQueries({ queryKey: ["moriarty-environments"] });
    },
  });
}

export function useSyncMoriartyModelCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => syncMoriartyModelCredentials(id),
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: ["moriarty-environment", id] });
      queryClient.invalidateQueries({ queryKey: ["moriarty-environments"] });
    },
  });
}
